<template>
  <div class="collection-stats-view">
    <empty-state
      v-if="showEmptyState"
      icon="analytics"
      title="You don't have any cards in this collection!"
      description="We calculate statistical updates to your collection at the end of each day after you add cards."
    />

    <template v-else>
      <graph-overview
        :stats="collectionStats.valueStats"
        :graph-values="[collectionStats.valueGraph, collectionStats.investmentGraph]"
        :zoom.sync="zoom"
        :scale.sync="scale"
        :empty-graph-text="emptyGraphText"
      />

      <div class="breakdowns">
        <pie-graph
          v-if="playerLabels.length"
          title="Player Breakdown"
          :labels="playerLabels"
          :datasets="playerDatasets"
        />

        <pie-graph
          v-if="categoryLabels.length"
          title="Category Breakdown"
          :labels="categoryLabels"
          :datasets="categoryDatasets"
        />
      </div>

      <div v-if="collectionStats.soldStats.length" class="sold-stats">
        <h3 class="secondary-font">
          Sold Stats
        </h3>

        <div class="grid">
          <stat-item
            v-for="(stat, i) in collectionStats.soldStats"
            :key="i"
            size="large"
            :success="stat.positive"
            :error="stat.negative"
            :label="stat.label"
            :value="stat.value"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex';

import GraphOverview from '@cardladder/shared/components/GraphOverview';
import PieGraph from '@cardladder/shared/components/PieGraph';
import StatItem from '@cardladder/shared/components/StatItem';

import { getCollectionStats } from '@cardladder/shared/utils/collection.util';

import { GRAPH_COLORS } from '@cardladder/shared/constants';

export default {
  components: {
    GraphOverview,
    PieGraph,
    StatItem,
  },

  data() {
    return {
      playerLabels: [],
      playerDatasets: [],
      categoryLabels: [],
      categoryDatasets: [],
    };
  },

  created() {
    this.setupData();
  },

  computed: {
    ...mapState({
      collection: (state) => state.collection,
    }),

    collectionStats() {
      return getCollectionStats(this.collection, this.zoom);
    },

    zoom: {
      get() {
        return this.$store.state.cardGraphZoom;
      },

      set(value) {
        this.$store.commit('SET_CARD_GRAPH_ZOOM', value);
      },
    },

    scale: {
      get() {
        return this.$store.state.cardGraphScale;
      },

      set(value) {
        this.$store.commit('SET_CARD_GRAPH_SCALE', value);
      },
    },

    showEmptyState() {
      return !this.collection.numCards;
    },

    emptyGraphText() {
      return this.collection && !this.collection.totalNumCards
        ? 'No Active Cards'
        : 'Check back tomorrow for more graphs and statistics!';
    },
  },

  methods: {
    setupData() {
      if (this.collection && this.collection.playerBreakdown) {
        this.playerLabels = this.collection.playerBreakdown.map((player) => player.label);
        this.playerDatasets = this.collection.playerBreakdown.map((player, i) => ({
          label: player.label,
          data: player.percentage ? (player.percentage * 100).toFixed(2) : 0,
          value: player.value || 0,
          borderColor: GRAPH_COLORS[i],
        }));
      }

      if (this.collection && this.collection.categoryBreakdown) {
        this.categoryLabels = this.collection.categoryBreakdown.map((category) => category.label);
        this.categoryDatasets = this.collection.categoryBreakdown.map((category, i) => ({
          label: category.label,
          data: category.percentage ? (category.percentage * 100).toFixed(2) : 0,
          value: category.value || 0,
          borderColor: GRAPH_COLORS[i],
        }));
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.breakdowns {
  margin-top: $gp * 2;
  display: grid;
  grid-gap: $gp;
  grid-template-columns: repeat(2, 1fr);

  @media($medium-down) {
    grid-auto-flow: column;
    grid-template-rows: repeat(2, 1fr);
    grid-template-columns: auto;
  }
}

.grid {
  display: grid;
  grid-gap: $gp;
  grid-template-columns: repeat(2, 1fr);

  @media($large-up) {
    grid-template-columns: repeat(5, 1fr);
  }
}

.sold-stats {
  margin-top: $gp * 2;
}
</style>
